import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

/*
 * PeriodFilter — the Visitors-style time-range control: a bordered chip
 * ("This week ⇅") opening a grouped popover of period presets, selected
 * item marked with a brand dot. Values match the reference exactly.
 */

export type PeriodKey =
    | 'today'
    | 'tomorrow'
    | 'this_week'
    | 'this_month'
    | 'this_year'
    | 'next_7'
    | 'next_30'
    | 'next_6m'
    | 'next_12m'
    | 'all_time';

export const PERIOD_GROUPS: { key: PeriodKey; label: string }[][] = [
    [
        { key: 'today', label: 'Today' },
        { key: 'tomorrow', label: 'Tomorrow' },
        { key: 'this_week', label: 'This week' },
        { key: 'this_month', label: 'This month' },
        { key: 'this_year', label: 'This year' },
    ],
    [
        { key: 'next_7', label: 'Next 7 days' },
        { key: 'next_30', label: 'Next 30 days' },
        { key: 'next_6m', label: 'Next 6 months' },
        { key: 'next_12m', label: 'Next 12 months' },
    ],
    [{ key: 'all_time', label: 'All time' }],
];

export function periodLabel(key: PeriodKey): string {
    for (const group of PERIOD_GROUPS) {
        const hit = group.find((o) => o.key === key);
        if (hit) return hit.label;
    }
    return 'Period';
}

interface PeriodFilterProps {
    value: PeriodKey;
    onChange: (key: PeriodKey) => void;
}

export default function PeriodFilter({ value, onChange }: PeriodFilterProps) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="ps-pill flex cursor-pointer items-center gap-2 px-4 font-[inherit] text-[13px] font-semibold text-foreground"
            >
                {periodLabel(value)}
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-faint"
                >
                    <path d="M3.5 4.5 6 2l2.5 2.5M3.5 7.5 6 10l2.5-2.5" />
                </svg>
            </button>

            {open && (
                <div
                    role="listbox"
                    className="absolute left-0 top-[calc(100%+6px)] z-40 w-[220px] rounded-2xl border border-border bg-background py-2 shadow-[0_18px_50px_-12px_rgba(20,23,29,0.25)]"
                >
                    <div className="px-4 pb-1 pt-1.5 text-[12.5px] text-faint">
                        Period
                    </div>
                    {PERIOD_GROUPS.map((group, gi) => (
                        <div
                            key={gi}
                            className={
                                gi > 0 ? 'mt-1.5 border-t border-line pt-1.5' : ''
                            }
                        >
                            {group.map((option) => (
                                <button
                                    key={option.key}
                                    type="button"
                                    role="option"
                                    aria-selected={value === option.key}
                                    onClick={() => {
                                        onChange(option.key);
                                        setOpen(false);
                                    }}
                                    className="flex w-full cursor-pointer items-center justify-between border-0 bg-transparent px-4 py-[7px] text-left font-[inherit] text-[13.5px] text-foreground hover:bg-sunk"
                                >
                                    {option.label}
                                    {value === option.key && (
                                        <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                                    )}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
